/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useEffect, useRef, useState } from 'react';
import { LegalDocument } from '@/schemas/schema';
import { generatePdfWithMetadata } from '@/modules/templates/services/pdf.service';
import { trackDocumentPreviewLoaded } from '@/lib/analytics';

interface UsePdfPreviewOptions {
  formData: Record<string, any>;
  templateSlug: string;
  /** The scrollable PDF viewport — used to preserve scroll position across regens. */
  scrollContainerRef: React.RefObject<HTMLElement | null>;
}

type Slot = 'a' | 'b';

/** How long to batch rapid edits before regenerating the PDF. */
const REGEN_DEBOUNCE_MS = 800;

/**
 * Owns the locally-generated PDF preview lifecycle with a two-slot double
 * buffer so edits never flash a loader or jump the scroll position.
 *
 * The visible (active) slot stays fully painted while a freshly generated blob
 * is parsed in the hidden (buffer) slot. When the buffer reports it has loaded,
 * we flip it to active and restore the captured scroll offset — so the swap is
 * seamless. The full-screen loader (`isLoadingPdf`) is used only for the very
 * first load / hard document-prop change, where there is no previous frame.
 */
export function usePdfPreview({ formData, templateSlug, scrollContainerRef }: UsePdfPreviewOptions) {
  const [slotUrls, setSlotUrls] = useState<Record<Slot, string | null>>({ a: null, b: null });
  const [activeSlot, setActiveSlot] = useState<Slot>('a');
  const [numPages, setNumPages] = useState<number | null>(null);
  const [isLoadingPdf, setIsLoadingPdf] = useState(false);
  const [isPdfStale, setIsPdfStale] = useState(false);
  const [apiSignatureFields, setApiSignatureFields] = useState<any[] | null>(null);

  // Mirror state into refs so the async generate path reads current values
  // without re-creating callbacks on every render.
  const slotUrlsRef = useRef(slotUrls);
  const activeSlotRef = useRef(activeSlot);
  useEffect(() => {
    slotUrlsRef.current = slotUrls;
  }, [slotUrls]);
  useEffect(() => {
    activeSlotRef.current = activeSlot;
  }, [activeSlot]);

  const regenTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const restoreScrollRef = useRef<number | null>(null);
  // The slot a background regen wrote into, waiting to be flipped to active.
  const pendingFlipRef = useRef<Slot | null>(null);
  // Resolves the promise returned by regenerateNow once the new preview has
  // fully rendered + swapped in (so callers can keep a spinner up until then).
  const flipResolveRef = useRef<(() => void) | null>(null);
  const resolveFlip = () => {
    const resolve = flipResolveRef.current;
    flipResolveRef.current = null;
    resolve?.();
  };

  const generatePreview = useCallback(
    async (documentToUse: LegalDocument, opts: { showLoader?: boolean } = {}) => {
      const { showLoader = true } = opts;

      if (showLoader) {
        setIsLoadingPdf(true);
      } else {
        // Background regen — remember the viewport so we can restore it after
        // the buffer flips in.
        restoreScrollRef.current = scrollContainerRef.current?.scrollTop ?? null;
        setIsPdfStale(true);
      }

      try {
        const result = await generatePdfWithMetadata(documentToUse, formData);
        const url = window.URL.createObjectURL(result.pdfBlob);

        if (result.signatureFields?.length) {
          setApiSignatureFields(result.signatureFields);
        }

        // showLoader → initial/hard load: render straight into the active slot.
        // Otherwise → render into the inactive (buffer) slot and flip on load.
        const target: Slot = showLoader
          ? activeSlotRef.current
          : activeSlotRef.current === 'a'
            ? 'b'
            : 'a';

        if (!showLoader) pendingFlipRef.current = target;

        const previous = slotUrlsRef.current[target];
        setSlotUrls((s) => ({ ...s, [target]: url }));
        if (previous) window.URL.revokeObjectURL(previous);

        // The blob is ready — drop the full-screen loader now so the viewer
        // (and its <Document>) actually mounts. Waiting for the Document's
        // onLoadSuccess here would deadlock: the viewer only renders while
        // !isLoadingPdf, so the load event that clears it could never fire.
        if (showLoader) setIsLoadingPdf(false);

        trackDocumentPreviewLoaded(templateSlug);
      } catch (error) {
        console.error('Error generating PDF preview:', error);
        restoreScrollRef.current = null;
        pendingFlipRef.current = null;
        if (showLoader) setIsLoadingPdf(false);
        setIsPdfStale(false);
        resolveFlip();
      }
    },
    [formData, templateSlug, scrollContainerRef]
  );

  /**
   * Called when a slot's <Document> finishes *parsing*. Used only to clear the
   * initial full-screen loader for the active slot — the buffer→active flip is
   * deferred to handleSlotReady (below) so we don't reveal blank pages.
   */
  const handleSlotLoaded = useCallback((slot: Slot, loadedNumPages: number) => {
    if (slot === activeSlotRef.current && pendingFlipRef.current !== slot) {
      setNumPages(loadedNumPages);
      setIsLoadingPdf(false);
    }
  }, []);

  /**
   * Called when a slot has finished *painting every page*. If it's the pending
   * background buffer, reveal it now (with scroll restored) — the canvases are
   * already drawn, so the swap is seamless with no blank flash.
   */
  const handleSlotReady = useCallback(
    (slot: Slot, readyNumPages: number) => {
      if (pendingFlipRef.current !== slot) return;
      pendingFlipRef.current = null;
      setActiveSlot(slot);
      setNumPages(readyNumPages);
      setIsPdfStale(false);

      const target = restoreScrollRef.current;
      restoreScrollRef.current = null;
      if (target != null) {
        // Restore after the flip commit so the now-active layer has laid out.
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (scrollContainerRef.current) scrollContainerRef.current.scrollTop = target;
          });
        });
      }

      // Let any caller awaiting this regeneration (e.g. the Save button) know
      // the updated preview is now on screen.
      resolveFlip();
    },
    [scrollContainerRef]
  );

  /** Debounced, loader-free regeneration — the default path for edits. */
  const scheduleRegeneration = useCallback(
    (documentToUse: LegalDocument) => {
      setIsPdfStale(true);
      if (regenTimerRef.current) clearTimeout(regenTimerRef.current);
      regenTimerRef.current = setTimeout(() => {
        regenTimerRef.current = null;
        void generatePreview(documentToUse, { showLoader: false });
      }, REGEN_DEBOUNCE_MS);
    },
    [generatePreview]
  );

  /**
   * Flush any pending debounce and regenerate immediately (still loader-free).
   * Returns a promise that resolves once the new preview has rendered and
   * swapped in — callers can keep a saving spinner up until then.
   */
  const regenerateNow = useCallback(
    (documentToUse: LegalDocument): Promise<void> => {
      if (regenTimerRef.current) {
        clearTimeout(regenTimerRef.current);
        regenTimerRef.current = null;
      }
      // Resolve any prior waiter so it can't hang if a new regen supersedes it.
      resolveFlip();
      return new Promise<void>((resolve) => {
        flipResolveRef.current = resolve;
        void generatePreview(documentToUse, { showLoader: false });
      });
    },
    [generatePreview]
  );

  // Cleanup timer + object URLs on unmount.
  useEffect(() => {
    return () => {
      if (regenTimerRef.current) clearTimeout(regenTimerRef.current);
      const { a, b } = slotUrlsRef.current;
      if (a) window.URL.revokeObjectURL(a);
      if (b) window.URL.revokeObjectURL(b);
    };
  }, []);

  return {
    slotUrls,
    activeSlot,
    numPages,
    isLoadingPdf,
    isPdfStale,
    apiSignatureFields,
    generatePreview,
    scheduleRegeneration,
    regenerateNow,
    handleSlotLoaded,
    handleSlotReady,
    /** True once the active slot has a URL to display. */
    hasPdf: !!slotUrls[activeSlot],
  };
}
